'use client';
import React from 'react';

const frameworks = [
  {
    name: 'SOC 2',
    description: 'Our DevSecOps Accelerator programme takes you from gap analysis to SOC2 Type I or Type II certification, with security controls embedded directly into your engineering practice.',
    features: ['Gap analysis and control mapping', 'Pipeline and IaC control implementation', 'Audit-ready evidence and documentation'],
  },
  {
    name: 'ISO 27001',
    description: 'We implement and maintain your ISMS aligned to ISO 27001:2022. From risk assessments and policy management to control implementation and internal audits.',
    features: ['ISMS design and implementation', 'Risk assessment and treatment', 'Policy and procedure development'],
  },
  {
    name: 'DORA',
    description: 'Meet Digital Operational Resilience Act requirements with ICT risk management frameworks, incident response procedures, and third-party oversight embedded in your DevSecOps practice.',
    features: ['ICT risk management framework', 'Incident response and reporting', 'Third-party risk oversight'],
  },
  {
    name: 'GDPR',
    description: 'Ensure data protection by design across your infrastructure and pipelines. We help you implement technical controls that satisfy GDPR requirements at the engineering level.',
    features: ['Data protection by design', 'Technical control implementation', 'Privacy impact assessments'],
  },
  {
    name: 'HIPAA',
    description: 'Protect patient data with security controls, access management, and audit trails aligned to HIPAA Security Rule requirements, built into your DevSecOps workflows.',
    features: ['PHI protection controls', 'Access management and audit trails', 'Security risk analysis'],
  },
  {
    name: 'PCI DSS',
    description: 'Secure cardholder data environments with continuous vulnerability management, network segmentation validation, and compliance controls embedded in your pipelines.',
    features: ['Cardholder data environment security', 'Network segmentation validation', 'Continuous vulnerability management'],
  },
];

export default function ComplianceSection() {
  return (
    <section className="bg-[#050a0f] border-t border-[#222] py-20 lg:py-28 relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-teal" style={{ width: '500px', height: '500px', top: '-50px', right: '-100px', opacity: 0.4 }} />
        <div className="orb orb-blue" style={{ width: '350px', height: '350px', bottom: '0', left: '-50px', opacity: 0.3 }} />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
          <div className="lg:w-1/2 fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">
              Compliance
            </p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight">
              Engineering-led compliance across every major framework
            </h2>
          </div>
          <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.15s' }}>
            <p className="text-[#aaa] text-base leading-relaxed">
              We specialise in SOC2 and ISO 27001, and support the full range of compliance frameworks your business needs to operate in regulated markets.
            </p>
          </div>
        </div>

        {/* Framework grid */}
        <div className="stagger-children grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[#222]">
          {frameworks?.map((fw, i) => (
            <div
              key={i}
              className="p-8 border-b border-r border-[#222] group hover:bg-[#181b1f] transition-all duration-300 shimmer-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="compliance-badge">{fw?.name}</span>
              </div>
              <p className="text-sm text-[#666] leading-relaxed mb-6">{fw?.description}</p>
              <ul className="flex flex-col gap-2">
                {fw?.features?.map((feature, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-[#80edfa] flex-shrink-0 pulse-dot" style={{ animationDelay: `${j * 0.3}s` }} />
                    <span className="text-xs text-[#aaa]">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
